import type { HistoryState } from '@tanstack/history'
import { redirect } from '@tanstack/react-router'

import type { GlobalRoleType, UserWithPermissions } from '@beaded/models'

import type { IAuthContext } from 'hooks/useUserContext'

import { type FeatureFlags, isFeatureEnabled } from './featureFlags'

type RouteContext = { auth: IAuthContext }
type BeforeLoadArgs = {
  context: RouteContext
  location: { pathname: string; search: Record<string, unknown>; hash: string }
}

export function requireAuth({ context, location }: BeforeLoadArgs) {
  const { auth } = context
  if (!auth.currentUser) {
    const hasSearch = Object.keys(location.search).length > 0
    throw redirect({
      to: '/login',
      search: {
        redirect: location.pathname,
        ...(hasSearch ? { redirectSearch: location.search } : {}),
        ...(location.hash ? { redirectHash: location.hash } : {}),
      },
    })
  }
}

export function requireGlobalUser({ context, location }: BeforeLoadArgs) {
  requireAuth({ context, location })
  const { auth } = context
  if (!auth.global.view) {
    throw redirect({
      to: auth.routing.home || '/',
      state: {
        from: location,
        reason: 'global-access-required',
      } as HistoryState,
    })
  }
}

export function requirePermission(
  checkFn: (permissions: UserWithPermissions['permissions'] | null) => boolean,
) {
  return ({ context, location }: BeforeLoadArgs) => {
    requireAuth({ context, location })
    const { auth } = context
    if (!checkFn(auth.permissions)) {
      throw redirect({
        to: auth.routing.home || '/',
        state: {
          from: location,
          reason: 'insufficient-permissions',
        } as HistoryState,
      })
    }
  }
}

export function requireFeatureFlag(flag: keyof FeatureFlags) {
  return ({ context, location }: BeforeLoadArgs) => {
    requireAuth({ context, location })
    if (!isFeatureEnabled(flag)) {
      throw redirect({
        to: (context.auth.routing.home as string) || '/',
      })
    }
  }
}

export function requireGlobalRole(...roles: GlobalRoleType[]) {
  return requirePermission(
    (permissions) =>
      permissions?.global?.some((p) =>
        roles.includes(p.role as GlobalRoleType),
      ) || false,
  )
}
